// leaseholderChangeClient — AUTHENTICATED staff client for the Leaseholder
// Change, Release & Assumption Agreement send surface (/send/leaseholder-change).
//
// Three endpoints, all Bearer-authed via the shared `api` helper (wire shapes
// mirror apps/api/src/routes/leaseholderChange.ts — keep in sync):
//
//   • GET  /api/leaseholder-change/candidates → LeaseholderChangeCandidates
//     APPLICATION-FIRST step 1: every APPROVED application group whose unit has
//     an active tenancy (incoming roommates joining an existing household).
//     Each candidate is the COMPLETE working set — current residents, resolved
//     lease start date, premises address, applicants — no second fetch needed.
//     `sendEnabled` reports the LEASEHOLDER_CHANGE_SEND_ENABLED gate so the
//     page can flag the dormant state up front.
//
//   • GET  /api/leaseholder-change/context?propertyId=<id>&unitId=<id>
//       → LeaseholderChangeContext
//     MANUAL FALLBACK for tenancies whose application carries no unit: the same
//     working set keyed by property/unit instead of by application.
//
//   • POST /api/leaseholder-change/initiate → InitiateLeaseholderChangeResult
//     Renders the fully-baked agreement, creates the DocuSeal submission, and
//     emails every resident-group signer their /sign/:uuid link. Pass
//     `applicationGroupId` when initiated from a candidate — it keys the
//     signature record to the household. Errors:
//       400 invalid input / duplicate signer email → { error, detail: string[] }
//       409 missing signer email(s)                → { error, detail: string[] }
//       503 feature dormant → LeaseholderChangeNotEnabledError

import { api, ApiError } from './api';

// ---------------------------------------------------------------------------
// Wire types (mirror apps/api)
// ---------------------------------------------------------------------------

/** One financially-responsible resident currently on the lease. */
export interface LeaseholderChangeResident {
  /** AppFolio tenant id (stable row key). */
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

/** One applicant on an approved application group. */
export interface LeaseholderChangeApplicant {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  isPrimary: boolean;
  /** yyyy-mm-dd; null when AppFolio has no DOB on file. */
  dateOfBirth: string | null;
  /** AppFolio's adult flag; null when unknown (fall back to dateOfBirth). */
  isAdult: boolean | null;
}

/** An APPROVED rental-application group on the property — the Additional
 *  Residents candidates. `unitId` may name another unit on the property (a
 *  group's unit assignment can lag the actual plan); the UI flags same-unit
 *  groups first. */
export interface LeaseholderChangeApprovedGroup {
  groupId: string;
  unitId: string | null;
  submittedAt: string | null;
  applicants: LeaseholderChangeApplicant[];
}

export interface LeaseholderChangeContext {
  residents: LeaseholderChangeResident[];
  /** yyyy-mm-dd, or '' when AppFolio carries none on file. */
  leaseStartDate: string;
  propertyAddress: string;
  approvedGroups: LeaseholderChangeApprovedGroup[];
  /** Whether LEASEHOLDER_CHANGE_SEND_ENABLED (+ WHP_OWN_MESSAGING) is on. */
  sendEnabled: boolean;
}

/** One application-first candidate: an APPROVED application group whose unit
 *  has an active tenancy. Carries the COMPLETE working set for the builder. */
export interface LeaseholderChangeCandidate {
  /** The application group id — sent back as `applicationGroupId` on initiate. */
  groupId: string;
  propertyId: string;
  unitId: string;
  propertyAddress: string;
  submittedAt: string | null;
  /** yyyy-mm-dd, or '' when AppFolio carries none on file. */
  leaseStartDate: string;
  /** The financially-responsible residents currently on the lease. */
  currentResidents: LeaseholderChangeResident[];
  /** The application's members — the incoming Additional Residents. */
  applicants: LeaseholderChangeApplicant[];
}

export interface LeaseholderChangeCandidates {
  candidates: LeaseholderChangeCandidate[];
  /** Whether LEASEHOLDER_CHANGE_SEND_ENABLED (+ WHP_OWN_MESSAGING) is on. */
  sendEnabled: boolean;
}

/** One party on the initiate body (original / additional / outgoing). */
export interface LeaseholderChangePerson {
  firstName: string;
  lastName: string;
  email: string;
}

export interface InitiateLeaseholderChangeBody {
  propertyId: string;
  unitId: string;
  propertyAddress: string;
  /** yyyy-mm-dd — the date the lease was ORIGINALLY signed (staff-confirmed). */
  originalLeaseDate: string;
  /** yyyy-mm-dd — the change takes effect at 11:59 p.m. on this date. */
  effectiveDate: string;
  original: LeaseholderChangePerson[];
  additional: LeaseholderChangePerson[];
  outgoing: LeaseholderChangePerson[];
  /** The incoming application group this change was initiated from (candidate
   *  flow) — keys the signature record to the household. Omit on the manual
   *  property/unit fallback path. */
  applicationGroupId?: string;
}

/** One signer on the created submission, echoed back by `initiate`. */
export interface LeaseholderChangeSubmitter {
  /** OriginalResident1..N / AdditionalResident1..N / OutgoingResident1..N / Lessor. */
  role: string;
  name?: string;
  email?: string;
  /** 0 = resident groups (parallel); 1 = Lessor (countersigns last). */
  order?: number;
  /** pending / sent / opened / completed / declined. */
  status: string;
  sentAt?: string;
}

export interface InitiateLeaseholderChangeResult {
  /** The synthetic `leaseholder-change-<ulid>` group id. */
  groupId: string;
  docusealSubmissionId: number;
  status: string;
  submitters: LeaseholderChangeSubmitter[];
  /** Resident-group signers emailed their signing link this run. */
  emailed: number;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when the initiate route answers 503 — the feature is gated off
 * (LEASEHOLDER_CHANGE_SEND_ENABLED / WHP_OWN_MESSAGING). The page catches this
 * to render the dormant state (mirrors ownerIntakeClient's NotEnabledError).
 */
export class LeaseholderChangeNotEnabledError extends Error {
  constructor(message = 'Leaseholder change sends are not enabled yet.') {
    super(message);
    this.name = 'LeaseholderChangeNotEnabledError';
  }
}

/** Read the `{ error, detail }` body off a 400/409 initiate rejection. */
export function leaseholderChangeErrorDetail(err: unknown): {
  message: string;
  detail: string[];
} | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { error?: string; detail?: unknown } | null;
  return {
    message: body?.error ?? `Could not send the agreement (HTTP ${err.status})`,
    detail: Array.isArray(body?.detail) ? (body.detail as string[]) : [],
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// The adult heuristic that decides whether an approved-group applicant is
// PRE-CHECKED as an Additional Resident moved to ./applicantAdult (pure, no
// `./api` import) when the ADD-ONLY adoption addendum needed the same rule.
// Re-exported here so this module's consumers are unchanged.
export { applicantLikelyAdult } from './applicantAdult';

// The dedup/matching helpers live in ./leaseholderChangeMatch (kept free of the
// `./api` import so they stay unit-testable under node:test/tsx). Re-exported
// here for discoverability alongside the wire types they operate on.
export {
  isSameLeaseholderPerson,
  partitionCandidatePeople,
  type PartitionedResident,
  type PartitionedCandidatePeople,
} from './leaseholderChangeMatch';

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** Load the application-first candidates (approved groups joining an existing
 *  tenancy). Each candidate is the complete working set for the builder. */
export function getLeaseholderChangeCandidates(): Promise<LeaseholderChangeCandidates> {
  return api
    .get<Partial<LeaseholderChangeCandidates>>('/api/leaseholder-change/candidates')
    .then((raw) => ({
      candidates: raw.candidates ?? [],
      sendEnabled: raw.sendEnabled ?? false,
    }));
}

/** MANUAL FALLBACK: load the builder's working set for one tenancy by
 *  property/unit (for applications that carry no unit assignment). */
export function getLeaseholderChangeContext(
  propertyId: string,
  unitId: string,
): Promise<LeaseholderChangeContext> {
  const qs = `propertyId=${encodeURIComponent(propertyId)}&unitId=${encodeURIComponent(unitId)}`;
  return api
    .get<Partial<LeaseholderChangeContext>>(`/api/leaseholder-change/context?${qs}`)
    .then((raw) => ({
      residents: raw.residents ?? [],
      leaseStartDate: raw.leaseStartDate ?? '',
      propertyAddress: raw.propertyAddress ?? '',
      approvedGroups: raw.approvedGroups ?? [],
      sendEnabled: raw.sendEnabled ?? false,
    }));
}

/**
 * Render + send the agreement to every party. Throws
 * LeaseholderChangeNotEnabledError on a 503 (feature dormant); 400/409
 * propagate as ApiError with `{ error, detail }` (read via
 * leaseholderChangeErrorDetail).
 */
export async function initiateLeaseholderChange(
  body: InitiateLeaseholderChangeBody,
): Promise<InitiateLeaseholderChangeResult> {
  try {
    const raw = await api.post<{
      groupId: string;
      docusealSubmissionId?: number;
      status?: string;
      submitters?: LeaseholderChangeSubmitter[];
      emailed?: number;
    }>('/api/leaseholder-change/initiate', body);
    return {
      groupId: raw.groupId,
      docusealSubmissionId: raw.docusealSubmissionId ?? 0,
      status: raw.status ?? '',
      submitters: raw.submitters ?? [],
      emailed: raw.emailed ?? 0,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new LeaseholderChangeNotEnabledError(
        (err.body as { error?: string } | null)?.error ??
          'Leaseholder change sends are not enabled yet.',
      );
    }
    throw err;
  }
}
